import React, { useEffect, useRef, useState } from 'react';
import {
  Users,
  BookOpen,
  MapPin,
  Star,
  UserPlus,
  CheckCheck,
  MessageSquare,
  Sparkles,
} from 'lucide-react';
import './CollaboratorsShowcase.css';

/**
 * CollaboratorsShowcase - Collaboration Flow Section
 * 3 slides: Post Opportunity → Review Applications → Collaborate
 * Same layout as PapersShowcase
 */
const CollaboratorsShowcase = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [activeSlide, setActiveSlide] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.2 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  // Auto-rotate slides
  useEffect(() => {
    if (!isVisible) return;
    const timer = setTimeout(() => {
      setActiveSlide((prev) => (prev + 1) % 3);
    }, 5000);
    return () => clearTimeout(timer);
  }, [isVisible, activeSlide]);

  // ========================================
  // SLIDE 1: Post a Collaboration Opportunity
  // ========================================
  const renderPostOpportunity = () => (
    <div className="cshow__slide cshow__slide--post">
      {/* Post Header */}
      <div className="cshow__post-header">
        <div className="cshow__post-author">
          <div className="cshow__avatar" style={{ background: '#0ea5e9' }}>SA</div>
          <div className="cshow__author-info">
            <span className="cshow__author-name">Dr. Sarah Ahmed</span>
            <span className="cshow__author-meta">Professor · Climate Science · Cambridge</span>
          </div>
        </div>
        <span className="cshow__post-time">Just now</span>
      </div>

      <div className="cshow__post-badge">
        <UserPlus size={10} />
        Looking for Collaborators
      </div>

      <h4 className="cshow__post-title">Co-author needed: Statistical analysis for climate modeling paper</h4>
      <p className="cshow__post-desc">Looking for a statistician experienced with time-series modeling and Bayesian methods to join as co-author on our Nature Climate Change submission.</p>

      <div className="cshow__post-details">
        <div className="cshow__detail-row">
          <span className="cshow__detail-label">Role:</span>
          <span className="cshow__detail-value">Co-author</span>
        </div>
        <div className="cshow__detail-row">
          <span className="cshow__detail-label">Timeline:</span>
          <span className="cshow__detail-value">3 months</span>
        </div>
        <div className="cshow__detail-row">
          <span className="cshow__detail-label">Target:</span>
          <span className="cshow__detail-value">Nature Climate Change</span>
        </div>
        <div className="cshow__detail-row">
          <span className="cshow__detail-label">Funding:</span>
          <span className="cshow__detail-value">EPSRC Grant-funded</span>
        </div>
      </div>

      <div className="cshow__post-skills">
        <span>R</span>
        <span>Python</span>
        <span>Bayesian Statistics</span>
        <span>Time-Series</span>
        <span>Climate Science</span>
      </div>

      {/* Application progress */}
      <div className="cshow__post-progress">
        <div className="cshow__prog-row">
          <span className="cshow__prog-label">Application progress</span>
          <span className="cshow__prog-count">4 / 5 slots filled</span>
        </div>
        <div className="cshow__prog-bar">
          <div className="cshow__prog-fill" style={{ width: '80%' }} />
        </div>
        <div className="cshow__prog-avatars">
          <div className="cshow__prog-avatar" style={{ background: '#374151' }}>RK</div>
          <div className="cshow__prog-avatar" style={{ background: '#f59e0b' }}>ML</div>
          <div className="cshow__prog-avatar" style={{ background: '#06b6d4' }}>JW</div>
          <div className="cshow__prog-avatar" style={{ background: '#ec4899' }}>NP</div>
          <span className="cshow__prog-more">1 spot left</span>
        </div>
      </div>

      <div className="cshow__post-footer">
        <div className="cshow__post-stats">
          <span><Users size={10} /> 14 interested</span>
          <span><MessageSquare size={10} /> 6 comments</span>
          <span><BookOpen size={10} /> 3 communities</span>
        </div>
        <span className="cshow__post-apply-count">4 applications</span>
      </div>
    </div>
  );

  // ========================================
  // SLIDE 2: Review Applications
  // ========================================
  const applicants = [
    { initials: 'RK', color: '#374151', name: 'Dr. Raj Kumar', title: 'Statistician', institution: 'MIT', pubs: 24, match: 96, hIndex: 18, skills: ['R', 'Bayesian'], cited: '1.2K' },
    { initials: 'ML', color: '#f59e0b', name: 'Prof. Maria Lopez', title: 'Data Scientist', institution: 'Stanford', pubs: 31, match: 91, hIndex: 22, skills: ['Python', 'ML'], cited: '2.1K' },
    { initials: 'JW', color: '#06b6d4', name: 'Dr. James Wu', title: 'Biostatistician', institution: 'Oxford', pubs: 18, match: 87, hIndex: 14, skills: ['SPSS', 'SAS'], cited: '890' },
    { initials: 'NP', color: '#ec4899', name: 'Dr. Nina Petrov', title: 'Applied Math', institution: 'ETH Zurich', pubs: 12, match: 82, hIndex: 10, skills: ['Julia', 'MATLAB'], cited: '620' },
  ];

  const renderReviewApplications = () => (
    <div className="cshow__slide cshow__slide--review">
      <div className="cshow__review-header">
        <span className="cshow__review-title">4 applications</span>
        <span className="cshow__review-context">Climate Modeling Paper · Co-author</span>
      </div>

      {/* Column labels */}
      <div className="cshow__app-labels">
        <span>Applicant</span>
        <span>Role</span>
        <span>Papers</span>
        <span>h-index</span>
        <span>Skills</span>
        <span>Match</span>
        <span></span>
      </div>

      <div className="cshow__applicants">
        {applicants.map((a, i) => (
          <div key={i} className={`cshow__app-row ${i === 0 ? 'cshow__app-row--top' : ''}`}>
            <div className="cshow__app-person">
              <div className="cshow__avatar cshow__avatar--sm" style={{ background: a.color }}>{a.initials}</div>
              <div className="cshow__app-person-info">
                <span className="cshow__app-name">{a.name}</span>
                <span className="cshow__app-inst">{a.institution}</span>
              </div>
            </div>
            <span className="cshow__app-role">{a.title}</span>
            <span className="cshow__app-pubs">{a.pubs}</span>
            <span className="cshow__app-hindex">{a.hIndex}</span>
            <div className="cshow__app-skills">
              {a.skills.map((s, j) => (
                <span key={j} className="cshow__app-skill-tag">{s}</span>
              ))}
            </div>
            <div className="cshow__app-match-cell">
              <div className="cshow__app-match-bar">
                <div className="cshow__app-match-fill" style={{ width: `${a.match}%` }} />
              </div>
              <span className="cshow__app-match">{a.match}%</span>
            </div>
            {i === 0 ? (
              <span className="cshow__btn cshow__btn--accept">Accept</span>
            ) : (
              <span className="cshow__btn">Review</span>
            )}
          </div>
        ))}
      </div>

      <div className="cshow__review-footer">
        <Sparkles size={10} />
        <span>Applicants ranked by AI match score based on skills, publications, and research interests</span>
      </div>
    </div>
  );

  // ========================================
  // SLIDE 3: Collaboration Started
  // ========================================
  const renderCollaborationStarted = () => (
    <div className="cshow__slide cshow__slide--started">
      {/* Header card with stats inline */}
      <div className="cshow__accepted-card">
        <div className="cshow__accepted-left">
          <div className="cshow__accepted-avatars">
            <div className="cshow__avatar cshow__avatar--overlap" style={{ background: '#0ea5e9', zIndex: 2 }}>SA</div>
            <div className="cshow__avatar cshow__avatar--overlap" style={{ background: '#f59e0b', zIndex: 1, marginLeft: '-10px' }}>RK</div>
          </div>
          <div className="cshow__accepted-info">
            <div className="cshow__accepted-names">
              <span>Dr. Sarah Ahmed</span>
              <span className="cshow__accepted-amp">&</span>
              <span>Dr. Raj Kumar</span>
            </div>
            <span className="cshow__accepted-sub">Climate Modeling Paper · Co-authors</span>
          </div>
        </div>
        <div className="cshow__accepted-right">
          <div className="cshow__accepted-chips">
            <span className="cshow__chip">96% match</span>
            <span className="cshow__chip">24 papers</span>
            <span className="cshow__chip">3 months</span>
          </div>
          <div className="cshow__accepted-badge">
            <CheckCheck size={11} />
            Matched
          </div>
        </div>
      </div>

      {/* Lab workspace */}
      <div className="cshow__lab-card">
        <div className="cshow__lab-top">
          <span className="cshow__lab-name">Climate Research Lab</span>
          <span className="cshow__lab-status">Active</span>
        </div>
        <div className="cshow__lab-tools">
          <div className="cshow__lab-tool"><MessageSquare size={11} /> Team Chat</div>
          <div className="cshow__lab-tool"><CheckCheck size={11} /> 3 Tasks</div>
          <div className="cshow__lab-tool"><BookOpen size={11} /> 5 Files</div>
          <div className="cshow__lab-tool"><Star size={11} /> 2 Papers</div>
        </div>
      </div>

      {/* Milestones */}
      <div className="cshow__milestones">
        <div className="cshow__milestone cshow__milestone--done">
          <div className="cshow__ms-dot" />
          <div className="cshow__ms-info">
            <span className="cshow__ms-text">Collaboration accepted</span>
            <span className="cshow__ms-time">2 hours ago</span>
          </div>
        </div>
        <div className="cshow__milestone cshow__milestone--done">
          <div className="cshow__ms-dot" />
          <div className="cshow__ms-info">
            <span className="cshow__ms-text">Lab workspace created</span>
            <span className="cshow__ms-time">2 hours ago</span>
          </div>
        </div>
        <div className="cshow__milestone cshow__milestone--active">
          <div className="cshow__ms-dot" />
          <div className="cshow__ms-info">
            <span className="cshow__ms-text">Dataset uploaded by Dr. Ahmed</span>
            <span className="cshow__ms-time">1 hour ago</span>
          </div>
        </div>
        <div className="cshow__milestone cshow__milestone--active">
          <div className="cshow__ms-dot" />
          <div className="cshow__ms-info">
            <span className="cshow__ms-text">Dr. Kumar started Bayesian analysis</span>
            <span className="cshow__ms-time">30 min ago</span>
          </div>
        </div>
        <div className="cshow__milestone">
          <div className="cshow__ms-dot" />
          <div className="cshow__ms-info">
            <span className="cshow__ms-text">Draft review & submission</span>
            <span className="cshow__ms-time">Upcoming</span>
          </div>
        </div>
      </div>

      <div className="cshow__ai-footer">
        <Sparkles size={10} />
        <span>AI matched based on skills, publications, and research interests</span>
      </div>
    </div>
  );

  const slides = [
    { render: renderPostOpportunity },
    { render: renderReviewApplications },
    { render: renderCollaborationStarted },
  ];

  return (
    <section
      ref={sectionRef}
      className={`cshow ${isVisible ? 'cshow--visible' : ''}`}
    >
      <div className="cshow__container">
        {/* Top - Header */}
        <div className="cshow__header">
          <div className="cshow__text">
            <h2 className="cshow__title">Find the right collaborators</h2>
            <p className="cshow__desc">
              Post opportunities, review applications with AI-powered matching, and start working together in a private lab — all in one place.
            </p>
          </div>
          <a href="/collaborators" className="cshow__link">
            Find Collaborators <span className="cshow__arrow">→</span>
          </a>
        </div>

        {/* Bottom - Full Width Rotating Slides */}
        <div className="cshow__cards">
          {slides.map((slide, index) => (
            <div
              key={index}
              className={`cshow__card ${index === activeSlide ? 'cshow__card--active' : ''} ${
                index < activeSlide ? 'cshow__card--prev' : ''
              } ${index > activeSlide ? 'cshow__card--next' : ''}`}
            >
              {slide.render()}
            </div>
          ))}

          {/* Slide Indicators */}
          <div className="cshow__indicators">
            {slides.map((_, i) => (
              <button
                key={i}
                className={`cshow__indicator ${i === activeSlide ? 'cshow__indicator--active' : ''}`}
                onClick={() => setActiveSlide(i)}
              >
                {i === activeSlide && (
                  <span className="cshow__indicator-progress" key={`prog-${activeSlide}`} />
                )}
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default CollaboratorsShowcase;
